import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, Building, MapPin, Calendar, DollarSign, 
  Gift, ChevronRight, Loader2, Check
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MACRO_STAGES = [
  { id: "stage_projeto", name: "Projeto / Preparação", order: 1 },
  { id: "stage_terraplanagem", name: "Terraplanagem", order: 2 },
  { id: "stage_fundacao", name: "Fundação", order: 3 },
  { id: "stage_estrutura", name: "Estrutura", order: 4 },
  { id: "stage_alvenaria", name: "Alvenaria", order: 5 },
  { id: "stage_eletrica", name: "Elétrica", order: 6 },
  { id: "stage_hidraulica", name: "Hidráulica", order: 7 },
  { id: "stage_reboco", name: "Reboco / Gesso", order: 8 },
  { id: "stage_piso", name: "Piso / Revestimentos", order: 9 },
  { id: "stage_pintura", name: "Pintura", order: 10 },
  { id: "stage_esquadrias", name: "Esquadrias / Vidros", order: 11 },
  { id: "stage_piscina", name: "Piscina", order: 12 },
  { id: "stage_paisagismo", name: "Paisagismo", order: 13 },
  { id: "stage_finalizacao", name: "Finalização / Limpeza / Entrega", order: 14 }
];

export default function ProjectCreatePage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    address: {
      street: "",
      city: "",
      state: "",
      zip_code: ""
    },
    total_budget: "",
    start_date: "",
    expected_end_date: "",
    selected_stages: MACRO_STAGES.map(s => s.id),
    has_bonus: false,
    bonus_type: "percentage",
    bonus_percentage: "5",
    bonus_fixed_value: ""
  });

  const handleStageToggle = (stageId) => {
    setFormData(prev => ({
      ...prev,
      selected_stages: prev.selected_stages.includes(stageId)
        ? prev.selected_stages.filter(id => id !== stageId)
        : [...prev.selected_stages, stageId].sort((a, b) => {
            const orderA = MACRO_STAGES.find(s => s.id === a)?.order || 0;
            const orderB = MACRO_STAGES.find(s => s.id === b)?.order || 0;
            return orderA - orderB;
          })
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Digite o nome do projeto");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        address: formData.address,
        total_budget: parseFloat(formData.total_budget) || 0,
        start_date: formData.start_date || null,
        expected_end_date: formData.expected_end_date || null,
        selected_stages: formData.selected_stages,
        early_delivery_bonus: formData.has_bonus ? {
          bonus_type: formData.bonus_type,
          percentage: formData.bonus_type === "percentage" ? parseFloat(formData.bonus_percentage) / 100 : null,
          fixed_value: formData.bonus_type === "fixed" ? parseFloat(formData.bonus_fixed_value) : null,
          conditions: ["all_stages_approved", "no_open_disputes"]
        } : null
      };

      const res = await fetch(`${API_URL}/api/projects`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const project = await res.json();
        toast.success("Projeto criado com sucesso!");
        navigate(`/project/${project.project_id}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || "Erro ao criar projeto");
      }
    } catch (error) {
      toast.error("Erro ao criar projeto");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen-mobile bg-white pb-8" data-testid="project-create-page">
      {/* Header */}
      <header className="sticky-header border-b border-neutral-100">
        <div className="px-5 h-14 flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-neutral-50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="font-semibold text-neutral-900">Nova Obra</h1>
            <p className="text-xs text-neutral-500">Etapa {step} de 4</p>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="h-1 bg-neutral-100">
        <div 
          className="h-full bg-neutral-900 transition-all duration-300"
          style={{ width: `${(step / 4) * 100}%` }}
        />
      </div>

      <main className="px-5 py-6">
        {/* Step 1: Basic Info */}
        {step === 1 && (
          <div className="space-y-6 animate-up">
            <div>
              <h2 className="text-xl font-semibold text-neutral-900 mb-2">Informações básicas</h2>
              <p className="text-neutral-500 text-sm">Comece definindo o nome e descrição do seu projeto</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Nome do Projeto *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Casa na Praia, Minha Casa..."
                  className="input-minimal"
                  data-testid="project-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Descrição
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descreva seu projeto..."
                  className="input-minimal h-24 resize-none py-3"
                />
              </div>
            </div>

            <Button
              onClick={() => formData.name.trim() ? setStep(2) : toast.error("Digite o nome do projeto")}
              className="btn-minimal btn-primary w-full"
            >
              Continuar
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Address */}
        {step === 2 && (
          <div className="space-y-6 animate-up">
            <div>
              <h2 className="text-xl font-semibold text-neutral-900 mb-2">Localização da obra</h2>
              <p className="text-neutral-500 text-sm">Onde a construção será realizada?</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Endereço
                </label>
                <input
                  type="text"
                  value={formData.address.street}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    address: { ...prev.address, street: e.target.value }
                  }))}
                  placeholder="Rua, número, bairro"
                  className="input-minimal"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Cidade
                  </label>
                  <input
                    type="text"
                    value={formData.address.city}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      address: { ...prev.address, city: e.target.value }
                    }))}
                    placeholder="Cidade"
                    className="input-minimal"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Estado
                  </label>
                  <input
                    type="text"
                    value={formData.address.state}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      address: { ...prev.address, state: e.target.value }
                    }))}
                    placeholder="UF"
                    className="input-minimal"
                    maxLength={2}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  CEP
                </label>
                <input
                  type="text"
                  value={formData.address.zip_code}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    address: { ...prev.address, zip_code: e.target.value }
                  }))}
                  placeholder="00000-000"
                  className="input-minimal"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={() => setStep(1)} className="btn-minimal btn-secondary flex-1">
                Voltar
              </Button>
              <Button onClick={() => setStep(3)} className="btn-minimal btn-primary flex-1">
                Continuar
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Timeline & Budget */}
        {step === 3 && (
          <div className="space-y-6 animate-up">
            <div>
              <h2 className="text-xl font-semibold text-neutral-900 mb-2">Cronograma e orçamento</h2>
              <p className="text-neutral-500 text-sm">Defina datas e valores estimados</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Orçamento Total Estimado
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400">R$</span>
                  <input
                    type="number"
                    value={formData.total_budget}
                    onChange={(e) => setFormData(prev => ({ ...prev, total_budget: e.target.value }))}
                    placeholder="0,00"
                    className="input-minimal pl-12"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Data de Início
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                    className="input-minimal"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Previsão de Término
                  </label>
                  <input
                    type="date"
                    value={formData.expected_end_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, expected_end_date: e.target.value }))}
                    className="input-minimal"
                  />
                </div>
              </div>

              {/* Bonus Section */}
              <div className="card-subtle p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Gift className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-neutral-900">Bônus por entrega antecipada</p>
                      <p className="text-sm text-neutral-500">Incentive o prestador</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, has_bonus: !prev.has_bonus }))}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      formData.has_bonus ? "bg-emerald-500" : "bg-neutral-200"
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      formData.has_bonus ? "translate-x-6" : "translate-x-0.5"
                    }`} />
                  </button>
                </div>

                {formData.has_bonus && (
                  <div className="mt-4 pt-4 border-t border-neutral-200 space-y-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setFormData(prev => ({ ...prev, bonus_type: "percentage" }))}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                          formData.bonus_type === "percentage" 
                            ? "bg-neutral-900 text-white" 
                            : "bg-neutral-100 text-neutral-600"
                        }`}
                      >
                        Percentual
                      </button>
                      <button
                        onClick={() => setFormData(prev => ({ ...prev, bonus_type: "fixed" }))}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                          formData.bonus_type === "fixed" 
                            ? "bg-neutral-900 text-white" 
                            : "bg-neutral-100 text-neutral-600"
                        }`}
                      >
                        Valor fixo
                      </button>
                    </div>

                    {formData.bonus_type === "percentage" ? (
                      <div className="relative">
                        <input
                          type="number"
                          value={formData.bonus_percentage}
                          onChange={(e) => setFormData(prev => ({ ...prev, bonus_percentage: e.target.value }))}
                          placeholder="5"
                          className="input-minimal pr-8"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400">%</span>
                      </div>
                    ) : (
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400">R$</span>
                        <input
                          type="number"
                          value={formData.bonus_fixed_value}
                          onChange={(e) => setFormData(prev => ({ ...prev, bonus_fixed_value: e.target.value }))}
                          placeholder="0,00"
                          className="input-minimal pl-12"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={() => setStep(2)} className="btn-minimal btn-secondary flex-1">
                Voltar
              </Button>
              <Button onClick={() => setStep(4)} className="btn-minimal btn-primary flex-1">
                Continuar
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Stages */}
        {step === 4 && (
          <div className="space-y-6 animate-up">
            <div>
              <h2 className="text-xl font-semibold text-neutral-900 mb-2">Etapas da obra</h2>
              <p className="text-neutral-500 text-sm">Selecione as etapas que seu projeto terá</p>
            </div>

            <div className="space-y-2">
              {MACRO_STAGES.map((stage) => {
                const isSelected = formData.selected_stages.includes(stage.id);
                return (
                  <button
                    key={stage.id}
                    onClick={() => handleStageToggle(stage.id)}
                    className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                      isSelected 
                        ? "border-neutral-900 bg-neutral-50" 
                        : "border-neutral-200 bg-white hover:border-neutral-300"
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      isSelected ? "bg-neutral-900" : "bg-neutral-100"
                    }`}>
                      {isSelected ? (
                        <Check className="w-4 h-4 text-white" />
                      ) : (
                        <span className="text-xs text-neutral-400">{stage.order}</span>
                      )}
                    </div>
                    <span className={`font-medium ${isSelected ? "text-neutral-900" : "text-neutral-600"}`}>
                      {stage.name}
                    </span>
                  </button>
                );
              })}
            </div>

            <p className="text-sm text-neutral-500 text-center">
              {formData.selected_stages.length} etapas selecionadas
            </p>

            <div className="flex gap-3">
              <Button onClick={() => setStep(3)} className="btn-minimal btn-secondary flex-1">
                Voltar
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={loading || formData.selected_stages.length === 0}
                className="btn-minimal btn-primary flex-1"
                data-testid="create-project-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Building className="w-4 h-4 mr-2" />
                    Criar Projeto
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
